import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAdminSitzung } from '../../admin/useAdminSitzung';
import { useDurchfuehrung } from '../../admin/durchfuehrungApi';
import { useSystemstatus } from '../../admin/systemApi';
import { formatiereGroesse } from '../../components/ExportFormatZeile';
import { Kennzahlen } from '../../components/Kennzahlen';
import { StatusZeichen } from '../../components/StatusZeichen';
import { SystemStatus } from '../../components/SystemStatus';
import { formatiereZeitpunkt } from '../../formatierung';

/**
 * Dashboard (§20.2).
 *
 * Es beantwortet die Frage „Was ist gerade los?" auf einen Blick und verweist für jede Handlung
 * weiter — gesteuert wird im Durchführungsmodus (§20.3). Deshalb hat diese Seite **keine**
 * Direktaktionen: zwei Orte für dieselbe unwiderrufliche Handlung wären eine Fehlerquelle.
 *
 * Die Zahlen stammen aus derselben Auskunft wie dort. Eine Stimmenverteilung ist nicht darunter
 * (§16); angezeigt werden ausschließlich die Beteiligungszahlen aus §15.
 */
export function UebersichtSeite(): ReactElement {
  const sitzung = useAdminSitzung();
  const stand = useDurchfuehrung();
  const systemstatus = useSystemstatus();

  const daten = stand.data;
  const veranstaltung = daten?.veranstaltung ?? null;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Dashboard</h1>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-medium">Aktive Veranstaltung</h2>
          {veranstaltung !== null && (
            <Link to="/verwaltung/durchfuehrung" className="text-akzent text-sm underline">
              Zur Durchführung
            </Link>
          )}
        </div>

        {veranstaltung === null ? (
          <p className="text-text-gedaempft text-sm">
            Derzeit ist keine Veranstaltung freigegeben oder laufend.{' '}
            <Link to="/verwaltung/veranstaltungen" className="text-akzent underline">
              Zu den Veranstaltungen
            </Link>
          </p>
        ) : (
          <>
            <p className="font-medium">{veranstaltung.bezeichnung}</p>
            <p className="text-text-gedaempft mt-1 text-sm">
              Code {veranstaltung.code} ·{' '}
              {veranstaltung.status === 'laufend' ? 'laufend' : 'freigegeben'}
            </p>

            <div className="mt-4">
              <Kennzahlen
                zahlen={[
                  { beschriftung: 'Angemeldet', wert: daten?.teilnehmer.angemeldet },
                  { beschriftung: 'Verbunden', wert: daten?.teilnehmer.verbunden },
                  { beschriftung: 'Sitzung getrennt', wert: daten?.teilnehmer.getrennt },
                ]}
              />
            </div>
          </>
        )}
      </section>

      {veranstaltung !== null && (
        <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
          <h2 className="mb-3 text-lg font-medium">Abstimmung</h2>

          {daten?.geoeffnet != null ? (
            <>
              <p className="font-medium">
                Abstimmung {daten.geoeffnet.nummer ?? '—'}: {daten.geoeffnet.ueberschrift}
              </p>
              <p className="mt-1">
                <StatusZeichen beschriftung="Geöffnet" tonart="erfolg" />
              </p>
              <div className="mt-4">
                {/* §15 — und ausdrücklich nicht mehr als das (§16). */}
                <Kennzahlen
                  zahlen={[
                    {
                      beschriftung: 'Berücksichtigte Teilnehmer',
                      wert: daten.beteiligung?.beruecksichtigteTeilnehmer,
                    },
                    {
                      beschriftung: 'Abgegebene Stimmen',
                      wert: daten.beteiligung?.abgegebeneStimmen,
                    },
                    {
                      beschriftung: 'Noch nicht abgegeben',
                      wert: daten.beteiligung?.nichtAbgegebeneStimmen,
                    },
                  ]}
                />
              </div>
            </>
          ) : daten?.naechste != null ? (
            <p className="text-sm">
              Bereit: <span className="font-medium">{daten.naechste.ueberschrift}</span>. Derzeit
              ist keine Abstimmung geöffnet.
            </p>
          ) : (
            <p className="text-text-gedaempft text-sm">
              Derzeit ist keine Abstimmung geöffnet und keine als bereit markiert.
            </p>
          )}
        </section>
      )}

      {daten !== undefined && daten.warnungen.length > 0 && (
        <section className="border-warnung/40 bg-flaeche-gedaempft rounded-xl border p-4">
          <h2 className="mb-2 text-lg font-medium">Hinweise</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {daten.warnungen.map((warnung) => (
              <li key={warnung.art}>{warnung.text}</li>
            ))}
          </ul>
          <p className="text-text-gedaempft mt-2 text-xs">
            Hinweise sind keine Sperren: Eine Abstimmung darf unabhängig von der Teilnehmerzahl
            geöffnet werden, und die Anwendung bewertet weder Beschlussfähigkeit noch Quoren.
          </p>
        </section>
      )}

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Serverstatus</h2>
        <SystemStatus />

        {/* Speicherplatz- und Backupwarnungen nach §20.2 und §32. Sie stehen hier in Kurzform;
            die vollständige Aufstellung steht im Systemstatus (§20.4). */}
        {systemstatus.data !== undefined && (
          <div className="mt-3">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-text-gedaempft">Speicherplatz frei</dt>
              <dd>{formatiereGroesse(systemstatus.data.speicher.freiBytes)}</dd>

              <dt className="text-text-gedaempft">Datenbank</dt>
              <dd>
                {formatiereGroesse(systemstatus.data.datenbank.groesseBytes)} ·{' '}
                {systemstatus.data.datenbank.integritaet === 'ok'
                  ? 'Integrität ohne Befund'
                  : 'Integritätsprüfung fehlgeschlagen'}
              </dd>

              <dt className="text-text-gedaempft">Letztes Backup</dt>
              <dd>
                {systemstatus.data.backups.letztesErfolgreiches === null
                  ? 'noch keines'
                  : formatiereZeitpunkt(systemstatus.data.backups.letztesErfolgreiches)}
              </dd>
            </dl>

            {systemstatus.data.warnungen.length > 0 && (
              <ul className="border-warnung/50 bg-warnung/10 mt-3 list-disc rounded-md border p-3 pl-8 text-sm">
                {systemstatus.data.warnungen.map((warnung) => (
                  <li key={warnung.text}>{warnung.text}</li>
                ))}
              </ul>
            )}

            <p className="text-text-gedaempft mt-2 text-xs">
              Vollständige Angaben im Bereich <strong>Systemstatus</strong>.
            </p>
          </div>
        )}
      </section>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Sitzung</h2>
        {sitzung.data === undefined ? (
          <p className="text-text-gedaempft text-sm">Keine Angaben verfügbar.</p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-text-gedaempft">Angemeldet seit</dt>
            <dd>{formatiereZeitpunkt(sitzung.data.angemeldetSeit)}</dd>

            <dt className="text-text-gedaempft">Letzte Aktivität</dt>
            <dd>{formatiereZeitpunkt(sitzung.data.letzteAktivitaet)}</dd>
          </dl>
        )}
        <p className="text-text-gedaempft mt-3 text-xs">
          Die Sitzung endet nach 30 Minuten ohne Aktivität. Automatische Abfragen und
          Socket.IO-Nachrichten verlängern sie nicht.
        </p>
      </section>
    </div>
  );
}
